import { useState, useEffect, useCallback } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  Switch,
  FormControlLabel,
  FormGroup,
  Link,
  CircularProgress,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { apiClient } from '@services/api';
import { toDisplayError } from '../../utils/apiError';

interface UserPreferences {
  emailNotifications?: boolean;
}

interface UserProfile {
  id: string;
  preferences?: UserPreferences | null;
}

/**
 * REV-491: the notification preferences that used to BE the whole Settings
 * page, now one tab of it.
 */
export default function NotificationsTab() {
  const { t } = useTranslation();

  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [emailNotifications, setEmailNotifications] = useState(false);

  const loadSettings = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const res = await apiClient.get<{ success: boolean; data: UserProfile }>('/v1/users/me');
      const prefs = res.data.preferences ?? {};
      setEmailNotifications(prefs.emailNotifications ?? false);
    } catch {
      setLoadError(t('settings.errors.loadFailed'));
    } finally {
      setIsLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void loadSettings();
  }, [loadSettings]);

  const onSave = async () => {
    setSaveError(null);
    setSaved(false);
    setIsSaving(true);
    try {
      await apiClient.put('/v1/users/me', {
        preferences: { emailNotifications },
      });
      setSaved(true);
    } catch (error: unknown) {
      setSaveError(toDisplayError(error, t('settings.errors.saveFailed')));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      {loadError && (
        <Alert severity="error" role="alert" sx={{ mb: 3 }}>
          {loadError}
        </Alert>
      )}

      {isLoading ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 4 }}>
          <CircularProgress size={24} aria-hidden />
          <Typography>{t('settings.loading')}</Typography>
        </Box>
      ) : (
        <>
          <Paper variant="outlined" sx={{ p: 3, mb: 4 }}>
            <Typography variant="h6" component="h2" gutterBottom>
              {t('settings.preferences.title')}
            </Typography>
            {saved && (
              <Alert severity="success" role="status" sx={{ mb: 2 }}>
                {t('settings.preferences.saved')}
              </Alert>
            )}
            {saveError && (
              <Alert severity="error" role="alert" sx={{ mb: 2 }}>
                {saveError}
              </Alert>
            )}
            <FormGroup sx={{ gap: 1, mb: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={emailNotifications}
                    onChange={(e) => setEmailNotifications(e.target.checked)}
                    disabled={isSaving}
                  />
                }
                label={t('settings.preferences.emailNotifications')}
              />
              <Typography variant="caption" color="text.secondary" sx={{ ml: 6, mt: -1 }}>
                {t('settings.preferences.emailNotificationsHelp')}
              </Typography>
            </FormGroup>
            <Button variant="contained" onClick={onSave} disabled={isSaving}>
              {isSaving ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                t('settings.preferences.save')
              )}
            </Button>
          </Paper>

          <Paper variant="outlined" sx={{ p: 3 }}>
            <Typography variant="h6" component="h2" gutterBottom>
              {t('settings.apiKeys.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('settings.apiKeys.description')}
            </Typography>
            <Link component={RouterLink} to="/orgs">
              {t('settings.apiKeys.link')}
            </Link>
          </Paper>
        </>
      )}
    </>
  );
}
